/**
 * IPT primitives — the parts that are specific to this module.
 *
 * The important one is `Def`. Every figure on every IPT screen ships with its
 * definition and its denominator, because the P3 checkpoint existed to stop a
 * number being presented that a client cannot interrogate. A metric block
 * without a definition tooltip and a sample line is not finished.
 */
import { useCallback, useRef, useState } from 'react';
import { AlertTriangle, Info, Plug } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Pill } from '@/components/nfa/NfaPrimitives';
import { PARTY, STAGE_LABEL, STAGE_TONE, int, num, toneColor } from './iptTheme';

export { Btn, Drawer, Empty, Field, Input, Panel, Pill, Select, Stat, inputStyle }
  from '@/components/nfa/NfaPrimitives';

/* ── popover plumbing ──────────────────────────────────────────────────────
 * The first version kept the panel permanently in the DOM at opacity-0. An
 * opacity-0 element still counts towards scrollWidth, so the tooltips on the
 * right-hand tiles pushed the document to 2172px against a 1920px viewport —
 * that was the phantom horizontal scrollbar and the blank band on the right.
 * The panel is now mounted only while open, and flipped when it would cross
 * the right edge.
 */
const usePopover = () => {
  const ref = useRef(null);
  const [state, setState] = useState({ open: false, flip: false });
  const open = useCallback(() => {
    const r = ref.current?.getBoundingClientRect();
    setState({ open: true, flip: Boolean(r && r.left > window.innerWidth - 340) });
  }, []);
  const close = useCallback(() => setState((s) => ({ ...s, open: false })), []);
  return { ref, ...state, bind: { onMouseEnter: open, onMouseLeave: close,
    onFocus: open, onBlur: close } };
};

/* ── definition tooltip ─────────────────────────────────────────────────── */
export const Def = ({ title, body, basis, sample, axis, version, T, testId }) => {
  const p = usePopover();
  return (
    <span ref={p.ref} className="relative inline-flex align-middle" tabIndex={0}
      data-testid={testId} style={{ outline: 'none' }} {...p.bind}>
      <Info className="w-3 h-3 cursor-help shrink-0" style={{ color: T.muted }} />
      {p.open && (
        <span
          className="pointer-events-none absolute top-full mt-1 z-[60] w-[290px] rounded-md p-2.5 shadow-xl text-left"
          style={{ background: T.isDark ? '#0B0F16' : '#10192B', color: '#E7EDF7',
                   border: `1px solid ${T.borderStrong}`,
                   left: p.flip ? 'auto' : 0, right: p.flip ? 0 : 'auto' }}>
          <span className="block text-[10.5px] font-bold tracking-wide uppercase"
            style={{ color: '#9FBCE8' }}>{title}</span>
          <span className="block text-[11px] leading-relaxed mt-1">{body}</span>
          {basis && (
            <span className="block text-[10px] mt-1.5" style={{ color: '#8FA7C8' }}>
              Basis: {basis}
            </span>
          )}
          {sample && (
            <span className="block text-[10px] mt-1 pt-1.5"
              style={{ color: '#C9D7EC', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
              {sample}
            </span>
          )}
          {axis && (
            <span className="block text-[9.5px] mt-1.5 font-mono" style={{ color: '#7E93B4' }}>
              axis: {axis}{version ? ` · ${version}` : ''}
            </span>
          )}
        </span>
      )}
    </span>
  );
};

/* ── a value tile that cannot be read without its basis ─────────────────── */
export const Tile = ({ label, value, sub, tone, T, def, testId, onClick, extra }) => (
  <div data-testid={testId} onClick={onClick}
    className={`rounded-lg px-3.5 py-3 transition-transform ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}
    style={{ background: T.surface, border: `1px solid ${T.border}` }}>
    <div className="flex items-start gap-1.5">
      <span className="text-[9.5px] uppercase tracking-[0.09em] leading-tight"
        style={{ color: T.muted }}>{label}</span>
      {def && <Def T={T} {...def} testId={testId ? `${testId}-def` : undefined} />}
    </div>
    <div className="text-[21px] font-semibold tabular-nums leading-tight mt-1"
      style={{ color: tone || T.ink, fontFamily: '"Space Grotesk", Inter, sans-serif' }}>
      {value}
    </div>
    {sub && <div className="text-[10px] mt-0.5" style={{ color: T.muted }}>{sub}</div>}
    {extra}
  </div>
);

/* ── the denominator line that goes under every metric block ───────────── */
export const Sample = ({ text, T, testId }) => (
  text ? (
    <p data-testid={testId} className="text-[10px] leading-relaxed mt-2 pt-2"
      style={{ color: T.muted, borderTop: `1px dashed ${T.border}` }}>{text}</p>
  ) : null
);

/* ── simulated-source badge, persistent and in the header ──────────────── */
export const SimBadge = ({ source, T, testId = 'ipt-sim-badge' }) => {
  const p = usePopover();
  if (!source || !source.simulated) return null;
  return (
    <span ref={p.ref} className="relative inline-flex" tabIndex={0} data-testid={testId}
      style={{ outline: 'none' }} {...p.bind}>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-wide cursor-help"
        style={{ background: T.tint(T.accent, '22'), color: T.accent,
                 border: `1px solid ${T.tint(T.accent, '55')}` }}>
        <Plug className="w-3 h-3" />{source.badge || 'SAP VIM: simulated source'}
      </span>
      {p.open && (
        <span className="pointer-events-none absolute right-0 top-full mt-1 z-[70] w-[330px] rounded-md p-2.5 shadow-xl"
          style={{ background: '#10192B', color: '#E7EDF7', border: `1px solid ${T.borderStrong}` }}>
          <span className="block text-[11px] leading-relaxed">{source.statement}</span>
          <span className="block text-[10px] mt-1.5" style={{ color: '#8FA7C8' }}>
            Affects: {(source.affects || []).join(' · ')}
          </span>
        </span>
      )}
    </span>
  );
};

/** Inline variant for a field whose value came from the simulator. */
export const SimMark = ({ source, T, label = 'simulated' }) => (
  source && source.simulated ? (
    <span className="ml-1 px-1 py-px rounded text-[8.5px] font-bold uppercase tracking-wide align-middle"
      style={{ background: T.tint(T.accent, '1A'), color: T.accent }}
      data-testid="ipt-sim-mark">{label}</span>
  ) : null
);

/* ── lifecycle + attribution chips ─────────────────────────────────────── */
export const StageChip = ({ stage, T, testId }) => {
  const c = toneColor(T, STAGE_TONE[stage] || 'muted');
  return (
    <span data-testid={testId}
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
      style={{ background: T.tint(c), color: c, border: `1px solid ${T.tint(c, '33')}` }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: c }} />
      {STAGE_LABEL[stage] || stage || '—'}
    </span>
  );
};

export const WaitingPill = ({ party, inferred, T, testId }) => {
  const p = PARTY[party] || PARTY.NEITHER;
  const c = toneColor(T, p.tone);
  return (
    <span data-testid={testId}
      className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9.5px] font-bold uppercase tracking-wide whitespace-nowrap"
      style={inferred
        ? { color: T.muted, border: `1px dashed ${T.borderStrong}`,
            background: `repeating-linear-gradient(135deg, ${T.tint(T.muted, '14')} 0 4px, transparent 4px 8px)` }
        : { background: T.tint(c, '1F'), color: c, border: `1px solid ${T.tint(c, '44')}` }}>
      {inferred ? 'unknown' : p.label}
    </span>
  );
};

/** The legend that keeps the two axes from reading as one encoding. */
export const AxisLegend = ({ axes, T, testId = 'ipt-axis-legend' }) => {
  if (!axes) return null;
  const rows = [
    ['Suspension', axes.suspension, T.primaryLight, 'solid',
      'Contractual policy. Applies to the net cycle time and the clock figures.'],
    ['Attribution', axes.attribution, T.accent, 'dashed',
      'Record of fact. Applies to the client / vendor / third-party split.'],
  ];
  return (
    <div data-testid={testId} className="rounded-md p-2.5 space-y-1.5"
      style={{ background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
      {rows.map(([name, obj, colour, kind, why]) => (
        <div key={name} className="flex items-start gap-2">
          <span className="mt-1 w-6 shrink-0" style={{
            borderTop: `3px ${kind} ${colour}`,
          }} />
          <div className="min-w-0">
            <span className="text-[10.5px] font-bold" style={{ color: T.ink }}>{name}</span>
            <span className="text-[9.5px] ml-1.5 font-mono" style={{ color: T.muted }}>
              {obj?.version || '—'}
            </span>
            <p className="text-[10px] leading-snug" style={{ color: T.body }}>{why}</p>
          </div>
        </div>
      ))}
      {axes.note && (
        <p className="text-[9.5px] leading-snug pt-1" style={{ color: T.muted }}>{axes.note}</p>
      )}
    </div>
  );
};

/* ── small helpers used across the three screens ──────────────────────── */
export const Days = ({ value, dp = 1, tone, T }) => (
  <span className="tabular-nums" style={{ color: tone || T.ink }}>{num(value, dp)}</span>
);

export const Bar = ({ segments, T, height = 10, testId }) => {
  const total = segments.reduce((a, s) => a + (s.value || 0), 0) || 1;
  return (
    <div data-testid={testId} className="flex w-full rounded-full overflow-hidden"
      style={{ height, background: T.surfaceAlt, border: `1px solid ${T.border}` }}>
      {segments.map((s, i) => (
        <div key={i} title={`${s.label}: ${num(s.value)}`}
          style={{
            width: `${(100 * (s.value || 0)) / total}%`,
            background: s.hatch
              ? `repeating-linear-gradient(135deg, ${s.colour} 0 3px, transparent 3px 6px)`
              : s.colour,
            borderRight: i < segments.length - 1 ? `1px solid ${T.surface}` : 'none',
          }} />
      ))}
    </div>
  );
};

export const Blocked = ({ reason, T, testId }) => (
  reason ? (
    <span data-testid={testId} className="inline-flex items-center gap-1 text-[10px]"
      style={{ color: T.red }}>
      <AlertTriangle className="w-3 h-3 shrink-0" /><span className="truncate">{reason}</span>
    </span>
  ) : null
);

export const InvoiceLink = ({ id, children, T, tab, testId }) => (
  <Link to={`/project-controls/cost/invoices/${id}${tab ? `?tab=${tab}` : ''}`}
    data-testid={testId} className="hover:underline font-semibold"
    style={{ color: T.primary }}>{children}</Link>
);

export const Count = ({ value }) => <span className="tabular-nums">{int(value)}</span>;
